// Shared patient value formatters — one source of truth for how demographics
// render across the Patients row and the Schedule modal (dates, sex, names).

export const parseDob = (v) => {
  if (!v) return null;
  const d = new Date(typeof v === "string" && v.length === 10 ? v + "T00:00:00" : v);
  return isNaN(d) ? null : d;
};

// 18/Sep/1949
export const fmtDob = (v) => {
  const d = parseDob(v);
  if (!d) return v ? String(v) : "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mon = d.toLocaleDateString("en-US", { month: "short" });
  return `${dd}/${mon}/${d.getFullYear()}`;
};

// Single canonical sex format everywhere: M / F / initial / —
export const fmtSex = (g) => {
  const s = (g || "").trim().toLowerCase();
  if (!s) return "—";
  if (s.startsWith("f")) return "F";
  if (s.startsWith("m")) return "M";
  return s[0].toUpperCase();
};

// Title-case on render rather than trusting source-system casing.
export const titleCaseName = (name = "") =>
  String(name).toLowerCase().replace(/(^|[\s'-])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase());

// Title-case a display name but keep clinical credential tokens uppercase
// (M.D., D.O., NP, PA-C, RN, …). Display only — never send this casing back.
const CREDENTIALS = new Set(["MD", "DO", "NP", "PA", "PAC", "RN", "DDS", "DMD", "DPM", "DC", "OD", "PHD", "LPN", "ARNP", "CRNA", "FNP"]);
export const titleCaseNameWithCreds = (name = "") => {
  if (!name) return "";
  return String(name)
    .split(/(,\s*)/)
    .map((seg) => {
      if (/^,\s*$/.test(seg)) return seg;
      return seg
        .split(/\s+/)
        .map((tok) => (CREDENTIALS.has(tok.replace(/[.\-]/g, "").toUpperCase()) ? tok.toUpperCase() : titleCaseName(tok)))
        .join(" ");
    })
    .join("");
};

// Display-only casing for ALL-CAPS provider / billing names; mixed case is returned as-is.
const NAME_KEEP = { MD: "MD", "M.D.": "M.D.", DO: "DO", "D.O.": "D.O.", NP: "NP", PA: "PA", "PA-C": "PA-C", DPM: "DPM", DDS: "DDS", DMD: "DMD", PHD: "PhD", APRN: "APRN", FNP: "FNP", RN: "RN", LCSW: "LCSW", "P.C.": "P.C.", PC: "PC", LLC: "LLC", PLLC: "PLLC", LLP: "LLP", INC: "Inc.", "INC.": "Inc.", JR: "Jr.", "JR.": "Jr.", SR: "Sr.", "SR.": "Sr.", II: "II", III: "III", IV: "IV" };
const NAME_LOWER = new Set(["of", "and", "the", "for", "at", "de", "del", "la", "van", "von"]);
const capPart = (p) => { if (!p) return p; const l = p.toLowerCase(); if (/^mc[a-z]/.test(l)) return `Mc${l[2].toUpperCase()}${l.slice(3)}`; return l[0].toUpperCase() + l.slice(1); };
export const displayName = (name) => {
  const s = String(name ?? "");
  if (!s || s !== s.toUpperCase() || !/[A-Z]/.test(s)) return s;
  return s.split(/(\s+)/).map((tok, i) => {
    if (/^\s+$/.test(tok)) return tok;
    const m = tok.match(/^([^A-Z0-9.]*)(.*?)([,;:)]*)$/i); const [, pre, core, post] = m || ["", "", tok, ""];
    if (NAME_KEEP[core]) return pre + NAME_KEEP[core] + post;
    if (i > 0 && NAME_LOWER.has(core.toLowerCase())) return pre + core.toLowerCase() + post;
    return pre + core.split(/([-'’])/).map((x) => (/^[-'’]$/.test(x) ? x : capPart(x))).join("") + post;
  }).join("");
};
